/* eslint-disable no-unused-expressions */
import { useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useFormikContext, FieldArray, Field } from "formik";
import classNames from "classnames";
import "./VerificationCodeInputFormik.scss";

const VerificationCodeInputFormik = ({ length = 6, type = "number", name }) => {
	const inputRefs = useRef([]);
	const { values, errors, setErrors, setFieldValue } = useFormikContext();

	useEffect(() => {
		if (!values || !values[name]) {
			inputRefs?.current[0]?.focus();
		}
	}, []);

	const handlePaste = useCallback(
		event => {
			const pasteData = event.clipboardData.getData("text").trim();

			if (!/^\d+$/.test(pasteData) || pasteData.length !== length) {
				event.preventDefault();
				return;
			}

			event.preventDefault();

			setFieldValue(name, pasteData);

			pasteData.split("").forEach((char, i) => {
				const el = inputRefs.current[i];
				if (el) {
					el.value = char;
				}
			});

			inputRefs.current[length - 1]?.focus();
		},
		[length, name, setFieldValue]
	);

	const handleChange = useCallback(
		(index, value) => {
			let singleChar = value.slice(0, 1);
			if (value?.length > 1) {
				singleChar = value.slice(1, 2);
			}

			let updatedValues = values[name] || "".padEnd(length, " ");
			updatedValues =
				updatedValues.substring(0, index) + singleChar + updatedValues.substring(index + 1);

			setFieldValue(name, updatedValues);

			if (singleChar && index < length - 1) {
				inputRefs?.current[index + 1]?.focus();
			}
		},
		[values]
	);

	const handleKeyDown = (index, event) => {
		setErrors({});
		event.target.select();

		const { key, ctrlKey, metaKey } = event;

		switch (key) {
			case "Backspace":
				if (index > 0) {
					inputRefs?.current[index - 1]?.focus();
					inputRefs?.current[index - 1]?.select();
				}
				break;

			case "ArrowLeft":
				if (index > 0) {
					inputRefs?.current[index - 1]?.focus();
					inputRefs?.current[index - 1]?.select();
				}
				break;

			case "ArrowRight":
				if (index < length - 1) {
					inputRefs?.current[index + 1]?.focus();
					inputRefs?.current[index + 1]?.select();
				}
				break;
			default:
				if (type === "number") {
					const isDigit = /^\d$/.test(key);
					const isShortcut = ctrlKey || metaKey;
					const isControlKey = ["Tab", "Enter", "Escape"].includes(key);

					if (!isDigit && !isShortcut && !isControlKey) {
						event.preventDefault();
					}
				}
				break;
		}
	};

	const handleFocus = event => {
		event.target.select();
	};

	return (
		<FieldArray
			name={name}
			render={() => (
				<div className="verification-code">
					{Array.from({ length }).map((_, index) => (
						<Field
							key={index}
							name={`${name}[${index}]`}
							render={({ field }) => {
								return (
									<input
										{...field}
										ref={el => (inputRefs.current[index] = el)}
										type={type}
										pattern="[0-9]*"
										maxLength={1}
										value={field.value || ""}
										onChange={e => handleChange(index, e.target.value)}
										onKeyDown={e => handleKeyDown(index, e)}
										onFocus={handleFocus}
										onClick={handleFocus}
										className={classNames({
											"verification-code__input": true,
											"verification-code__input--error": Boolean(
												errors[name]
											),
										})}
										onPaste={handlePaste}
										data-testid={`verification-code-input-${index + 1}`}
									/>
								);
							}}
						/>
					))}
				</div>
			)}
		/>
	);
};

VerificationCodeInputFormik.propTypes = {
	length: PropTypes.number,
	type: PropTypes.string,
	name: PropTypes.string,
};
export default VerificationCodeInputFormik;
